import { Extension } from '@tiptap/core'
import { DOMSerializer, Fragment, Slice } from '@tiptap/pm/model'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { handlePaste as pasteCells, isInTable } from '@tiptap/pm/tables'
import { inGridSkippingTable } from './tableGrid.js'

/**
 * Copy and paste inside the Studio without loss (RED-317 PR 3b).
 *
 * The clipboard carries HTML, and every verbatim attr (pPr, rPr, tcPr, a
 * chart's whole drawing XML) is unrendered: pasted back through the parse
 * rules, a cut paragraph would lose its Word formatting and a cut chart card
 * would vanish. So a copy also writes the slice's JSON onto the first
 * element of the clipboard HTML, and a paste that finds it inserts exactly
 * that slice. Other programs ignore the attribute.
 *
 * The JSON is only trusted with this browser's own token next to it: a page
 * that puts crafted JSON on the clipboard could otherwise hand the compiler
 * verbatim XML of its choosing. Without the token (another browser, cleared
 * storage) the paste is ordinary HTML.
 */

const FRAGMENT_ATTRIBUTE = 'data-studio-fragment'
const TOKEN_KEY = 'redata.studio.clipboard-token'

let fallbackToken = null

function clipboardToken() {
  try {
    let token = window.localStorage.getItem(TOKEN_KEY)
    if (!token) {
      token = window.crypto.randomUUID()
      window.localStorage.setItem(TOKEN_KEY, token)
    }
    return token
  } catch {
    fallbackToken ??= window.crypto.randomUUID()
    return fallbackToken
  }
}

/** The slice from clipboard HTML written by copy, or null (not ours, not this browser's, not this schema's). */
export function studioSliceFromHtml(html, schema) {
  if (!html || !html.includes(FRAGMENT_ATTRIBUTE)) return null
  const element = new window.DOMParser().parseFromString(html, 'text/html').querySelector(`[${FRAGMENT_ATTRIBUTE}]`)
  try {
    const payload = JSON.parse(element?.getAttribute(FRAGMENT_ATTRIBUTE) ?? 'null')
    if (!payload || payload.token !== clipboardToken()) return null
    let content = Fragment.fromJSON(schema, payload.content)
    let { openStart, openEnd } = payload
    // The context prosemirror-view unwrapped before serialising, wrapped back on.
    for (let index = payload.context.length - 2; index >= 0; index -= 2) {
      const type = schema.nodes[payload.context[index]]
      if (!type) return null
      content = Fragment.from(type.create(payload.context[index + 1], content))
      openStart++
      openEnd++
    }
    const slice = new Slice(content, openStart, openEnd)
    slice.content.forEach((node) => node.check())
    return slice
  } catch {
    return null
  }
}

export const StudioClipboard = Extension.create({
  name: 'studioClipboard',

  // Its paste runs before StudioPaste's and prosemirror-tables'.
  priority: 1000,

  addProseMirrorPlugins() {
    const schema = this.editor.schema
    const serializer = DOMSerializer.fromSchema(schema)
    // serializeForClipboard unwraps single-child context before calling the
    // serializer; transformCopied sees the slice first, so it is kept here.
    let copied = null

    return [
      new Plugin({
        key: new PluginKey('studioClipboard'),
        props: {
          transformCopied(slice) {
            copied = slice
            return slice
          },
          clipboardSerializer: {
            serializeFragment(content, options, target) {
              const dom = serializer.serializeFragment(content, options, target)
              const first = dom.firstChild
              if (copied && first?.nodeType === 1) {
                // The same unwrapping serializeForClipboard did to get `content`.
                const unwrapped = []
                let { openStart, openEnd } = copied
                let inner = copied.content
                while (openStart > 1 && openEnd > 1 && inner.childCount === 1 && inner.firstChild.childCount === 1) {
                  openStart--
                  openEnd--
                  unwrapped.push(inner.firstChild.type.name, inner.firstChild.attrs)
                  inner = inner.firstChild.content
                }
                if (inner.eq(content)) {
                  first.setAttribute(
                    FRAGMENT_ATTRIBUTE,
                    JSON.stringify({ token: clipboardToken(), content: content.toJSON(), openStart, openEnd, context: unwrapped }),
                  )
                }
              }
              copied = null
              return dom
            },
          },
          handlePaste(view, event) {
            const slice = studioSliceFromHtml(event.clipboardData?.getData('text/html'), view.state.schema)
            if (!slice) return false
            // Copied cells pasted into a table: prosemirror-tables places them in the grid.
            if (isInTable(view.state) && !inGridSkippingTable(view.state) && pasteCells(view, event, slice)) return true
            view.dispatch(view.state.tr.replaceSelection(slice).scrollIntoView().setMeta('paste', true).setMeta('uiEvent', 'paste'))
            return true
          },
        },
      }),
    ]
  },
})
